import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import type { AbAssignment, AbVariant } from "../lib/admin-experiments-api";
import { fetchActiveExperiment, recordExperimentEvent } from "../lib/admin-experiments-api";

const IS_DEV = process.env.NODE_ENV !== "production";

const POLL_INTERVAL_MS = 30_000;
const OVERRIDE_STORAGE_KEY = "cf_ab_override";

type OverrideMap = Record<string, AbVariant>;

function readOverrides(): OverrideMap {
  try {
    return JSON.parse(localStorage.getItem(OVERRIDE_STORAGE_KEY) ?? "{}") as OverrideMap;
  } catch {
    return {};
  }
}

function writeOverride(slug: string, variant: AbVariant | null) {
  const map = readOverrides();
  if (variant === null) {
    delete map[slug];
  } else {
    map[slug] = variant;
  }
  localStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(map));
}

export type UseAbExperimentResult = {
  /** The server-assigned (or admin-overridden) variant, or null while loading / not in experiment. */
  variant: AbVariant | null;
  /** Human-readable label for the current variant ("Classic", "New", etc.) */
  variantLabel: string | null;
  /** Full experiment object if one is active for this page. */
  assignment: AbAssignment | null;
  /** Whether the initial fetch is still in flight. */
  isLoading: boolean;
  /** Admin-only: manually force a specific variant. Pass null to clear the override. */
  setOverride: (variant: AbVariant | null) => void;
  /** Record a conversion event for the current variant. */
  recordConversion: (metadata?: Record<string, unknown>) => void;
};

/**
 * Fetches the active A/B experiment for the current page and returns the
 * assigned variant.  Polls every 30 s for config changes (hot-reload).
 *
 * Pass `enabled: false` to skip fetching entirely (e.g. for unauthenticated
 * users).  Admin users may call `setOverride` to manually toggle variants;
 * regular users always see their server-assigned variant silently.
 */
export function useAbExperiment({
  enabled = true,
}: { enabled?: boolean } = {}): UseAbExperimentResult {
  const location = useLocation();
  const pathname = location.pathname;

  const [assignment, setAssignment] = useState<AbAssignment | null>(null);
  const [adminOverride, setAdminOverride] = useState<AbVariant | null>(() => {
    // Read any existing override for this path from localStorage. We don't
    // know the slug yet so we seed it lazily after the first fetch.
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const slugRef = useRef<string | null>(null);
  // Tracks which experiment ID we've already recorded an impression for on this page visit.
  // Resets when pathname changes (the useEffect below resets it on re-run).
  const impressionRecordedForRef = useRef<number | null>(null);
  // Tracks AbortControllers created for poll ticks so they can be aborted on unmount.
  const pollControllersRef = useRef<AbortController[]>([]);

  const load = useCallback(
    async (signal: AbortSignal) => {
      try {
        const result = await fetchActiveExperiment(pathname, signal);
        if (signal.aborted) return;
        setAssignment(result);
        if (result) {
          slugRef.current = result.experiment.slug;
          const overrides = readOverrides();
          const stored = overrides[result.experiment.slug];
          if (stored) setAdminOverride(stored);
          // Record impression once per experiment per page visit (not on every poll tick).
          if (result.experiment.id !== impressionRecordedForRef.current) {
            impressionRecordedForRef.current = result.experiment.id;
            void recordExperimentEvent({
              experimentId: result.experiment.id,
              variant: result.variant,
              event: "impression",
            });
          }
        } else {
          slugRef.current = null;
        }
      } catch (err) {
        if (IS_DEV) console.warn("[useAbExperiment] fetch error:", err);
        // Network errors should not leave the hook stuck in loading state
      } finally {
        if (!signal.aborted) setIsLoading(false);
      }
    },
    [pathname]
  );

  // Initial fetch + poll — skipped entirely when not enabled (e.g. unauthenticated)
  useEffect(() => {
    impressionRecordedForRef.current = null;
    if (!enabled) {
      setIsLoading(false);
      setAssignment(null);
      return;
    }
    setIsLoading(true);
    setAdminOverride(null);
    const controller = new AbortController();
    void load(controller.signal);
    const timer = setInterval(() => {
      const pollController = new AbortController();
      pollControllersRef.current.push(pollController);
      void load(pollController.signal);
    }, POLL_INTERVAL_MS);
    return () => {
      controller.abort();
      pollControllersRef.current.forEach((c) => c.abort());
      pollControllersRef.current = [];
      clearInterval(timer);
    };
  }, [load, enabled]);

  const setOverride = useCallback((variant: AbVariant | null) => {
    setAdminOverride(variant);
    if (slugRef.current) writeOverride(slugRef.current, variant);
  }, []);

  const recordConversion = useCallback(
    (metadata?: Record<string, unknown>) => {
      if (!assignment) return;
      // Skip recording when an admin override is active to avoid polluting analytics
      if (adminOverride !== null) return;
      void recordExperimentEvent({
        experimentId: assignment.experiment.id,
        variant: assignment.variant,
        event: "conversion",
        metadata,
      });
    },
    [assignment, adminOverride]
  );

  const effectiveVariant = adminOverride ?? assignment?.variant ?? null;
  const variantLabel =
    effectiveVariant === "a"
      ? (assignment?.experiment.variantALabel ?? null)
      : effectiveVariant === "b"
        ? (assignment?.experiment.variantBLabel ?? null)
        : null;

  return {
    variant: effectiveVariant,
    variantLabel,
    assignment,
    isLoading,
    setOverride,
    recordConversion,
  };
}
